import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ArtifactRef, ArtifactTarget, DraftEntry } from './contracts';
import { useApplicationChat } from '../context';
import { selectBoundArtifactProjection, selectAuthoritativeCanvasTabs } from '../../projection/canvas/artifactBindingProjection.js';
import { useSave } from './useSave';
import { useSelection } from './useSelection';

type Bindings = { [tab: string]: string };
interface EditorScope {
  drafts: { [tab: string]: DraftEntry };
}
const emptyEditor = (): EditorScope => ({ drafts: {} });
const scopedView = <T,>(values: { [key: string]: T }, bindings: Bindings): { [tab: string]: T } => (
  Object.fromEntries(Object.entries(bindings).filter(([, key]) => Object.prototype.hasOwnProperty.call(values, key))
    .map(([tab, key]) => [tab, values[key]]))
);
const useArtifactOwner = () => {
  const { realApi, viewModel, workspaceLocation } = useApplicationChat();
  const sessionId = viewModel.sessionList.activeSessionId;
  const sessionContext = useMemo(() => viewModel.session?.context ?? {}, [viewModel.session?.context]);
  const tenants = [...new Set((sessionContext.artifact_context ?? []).map((row: { artifact_ref?: { scope?: { tenant_id?: string } } }) => row.artifact_ref?.scope?.tenant_id).filter(Boolean))];
  const selection = useSelection({ sessionId, projectId: workspaceLocation?.effectiveProjectId ?? '',
    projectAPI: realApi.projectAPI, tenantId: tenants.length === 1 ? tenants[0] as string : undefined, sessionContext });
  const select = selection.select;
  const openArtifact = useCallback((action: { artifact_target?: ArtifactTarget; artifact_id?: string }) => {
    const matches = selectAuthoritativeCanvasTabs(sessionContext.artifact_tab_projection)
      .filter((tab) => action.artifact_id && tab.artifact_id === action.artifact_id)
      .map((tab) => selectBoundArtifactProjection(sessionContext, tab.key)?.artifact_ref).filter(Boolean);
    const reference: ArtifactRef | undefined = matches.length === 1 ? matches[0] : undefined;
    // Compatibility for an ID-only legacy card requires exactly one authoritative current reference.
    const target = action.artifact_target ?? (reference && { ...reference, project_id: reference.scope.project_id });
    return select(target as ArtifactTarget);
  }, [sessionContext, select]);
  const selected = selection.selection?.context;
  // This composed value is presentation-only. It never replaces viewModel.session.context.
  const presentationContext = useMemo(() => selected ? { ...sessionContext, ...selected,
    artifact_context: [...(sessionContext.artifact_context ?? []).filter((row: { artifact_id: string }) =>
      row.artifact_id !== selection.selection?.artifactRef.artifact_id), ...selected.artifact_context] } : sessionContext,
  [sessionContext, selected, selection.selection]);
  // An unresolved read may retain display data, but cannot provide a new composer target.
  const interactionContext = useMemo(() => ['pending', 'failed'].includes(selection.status) ? null : presentationContext,
    [selection.status, presentationContext]);
  const bindingSignature = JSON.stringify(Object.fromEntries(selectAuthoritativeCanvasTabs(presentationContext.artifact_tab_projection)
    .map(({ key }) => [key, selectBoundArtifactProjection(presentationContext, key)?.artifact_ref])
    .filter(([, reference]) => reference)
    .map(([tab, reference]) => [tab, JSON.stringify([tab, reference])])));
  const bindings = useMemo(() => JSON.parse(bindingSignature) as Bindings, [bindingSignature]);
  // Private keys include the immutable reference; the public editor API keeps its existing tab keys.
  const [sessions, setSessions] = useState<{ [id: string]: EditorScope }>({});
  const hydration = useRef(new Map<string, { current: string }>());
  const editor = sessions[sessionId] ?? emptyEditor();
  const updateEditor = useCallback((update: (value: EditorScope) => EditorScope) => {
    setSessions((values) => ({ ...values, [sessionId]: update(values[sessionId] ?? emptyEditor()) }));
  }, [sessionId]);
  const setDraft = useCallback((tab: string, patch: Partial<DraftEntry>) => {
    const key = bindings[tab];
    if (!key) return;
    updateEditor((value) => ({ ...value, drafts: { ...value.drafts,
      [key]: { ...(value.drafts[key] ?? { content: '', editing: false }), ...patch } } }));
  }, [bindings, updateEditor]);
  const refresh = viewModel.session.onRefresh;
  const acceptSaved = selection.acceptSaved;
  const onCommitted = useCallback(async (acknowledgment: import('./contracts').ArtifactEditResponse, expectedBase: ArtifactRef) => {
    acceptSaved(acknowledgment, expectedBase);
    // Host refresh rejects failed operations and returns an unwrapped SessionRecord, not an OperationResult.
    await refresh();
  }, [acceptSaved, refresh]);
  const saved = useSave({ sessionId, projectAPI: realApi.projectAPI, refresh, onCommitted });
  const hydrationRef = (tab: string) => {
    const key = JSON.stringify([sessionId, tab, bindings[tab]]);
    if (!hydration.current.has(key)) hydration.current.set(key, { current: '' });
    return hydration.current.get(key)!;
  };
  return { ...saved, selection, openArtifact, clearSelection: selection.clear, presentationContext, interactionContext, drafts: scopedView(editor.drafts, bindings),
    setDraft,
    lastHydratedIntakeContentRef: hydrationRef('requirement'),
    lastHydratedAnalysisContentRef: hydrationRef('analysis') };
};
const ArtifactsContext = createContext<ReturnType<typeof useArtifactOwner> | null>(null);
export const ArtifactsProvider = ({ children }: { children?: ReactNode }) => {
  const value = useArtifactOwner();
  return <ArtifactsContext.Provider value={value}>{children}</ArtifactsContext.Provider>;
};
export const useArtifacts = () => {
  const value = useContext(ArtifactsContext);
  if (!value) throw new Error('Artifact editing requires the application provider');
  return value;
};
export const useArtifactDraft = (tab: string) => {
  const { drafts, setDraft } = useArtifacts();
  const draft = drafts[tab];
  const setCanvasDraftEditing = useCallback((editing: boolean) => setDraft(tab, { editing }), [setDraft, tab]);
  const setCanvasDraftEditorValue = useCallback((content: string) => setDraft(tab, { content }), [setDraft, tab]);
  return useMemo(() => ({ canvasDraftEditing: draft?.editing ?? false, canvasDraftEditorValue: draft?.content ?? '',
    setCanvasDraftEditing, setCanvasDraftEditorValue }), [draft, setCanvasDraftEditing, setCanvasDraftEditorValue]);
};
